/**
 * WazeStyleNav - Waze-like turn-by-turn navigation interface
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigationStore } from '@/stores/useNavigationStore';
import { useLocationStore } from '@/stores/useLocationStore';
import { useAirQualityStore } from '@/stores';
import { useRunningStore } from '@/stores/useRunningStore';
import { useEnvironmentalSampling } from '@/hooks/useEnvironmentalSampling';
import { voiceGuidance } from '@/services/navigation/voiceGuidance';
import { calculateDistance } from '@/utils/cameraAnimation';
import { cn } from '@/utils/cn';

interface WazeStyleNavProps {
  className?: string;
}

export function WazeStyleNav({ className }: WazeStyleNavProps) {
  const isNavigating = useNavigationStore((state) => state.isNavigating);
  const activeRoute = useNavigationStore((state) => state.activeRoute);
  const currentStepIndex = useNavigationStore((state) => state.currentStepIndex);
  const isDemoMode = useNavigationStore((state) => state.isDemoMode);
  const advanceStep = useNavigationStore((state) => state.advanceStep);
  const stopNavigation = useNavigationStore((state) => state.stopNavigation);
  const updateProgress = useNavigationStore((state) => state.updateProgress);
  const aqi = useAirQualityStore((state) => state.currentReading?.aqi) || 42;

  const currentPosition = useLocationStore((state) => state.currentPosition);
  const speed = useLocationStore((state) => state.speed);

  // Recording state
  const isRecording = useRunningStore((s) => s.isRecording);
  const recordingData = useRunningStore((s) => s.recordingData);
  const [recElapsed, setRecElapsed] = useState(0);

  // Recording timer
  useEffect(() => {
    if (!isRecording || !recordingData.startTime) return;
    const tick = setInterval(() => {
      const rd = useRunningStore.getState().recordingData;
      if (!rd.startTime || rd.isPaused) return;
      const active = Math.floor((Date.now() - rd.startTime - rd.pausedDuration) / 1000);
      setRecElapsed(active);
    }, 500);
    return () => clearInterval(tick);
  }, [isRecording, recordingData.startTime]);

  // Environmental sampling — AQI + weather every 30s during recording
  useEnvironmentalSampling(isRecording);

  // GPS route tracking — poll location store and add route points
  const lastRecPosRef = useRef<{ lat: number; lng: number; ts: number } | null>(null);
  useEffect(() => {
    if (!isRecording) return;
    const gpsInterval = setInterval(() => {
      const { currentPosition, timestamp } = useLocationStore.getState();
      if (!currentPosition || !timestamp) return;
      const last = lastRecPosRef.current;
      if (!last || last.lat !== currentPosition.lat || last.lng !== currentPosition.lng || timestamp !== last.ts) {
        const currentAqi = useAirQualityStore.getState().currentReading?.aqi;
        useRunningStore.getState().addRoutePoint({
          lat: currentPosition.lat,
          lng: currentPosition.lng,
          elevation: undefined,
          timestamp: new Date(timestamp).toISOString(),
          aqi: currentAqi || undefined,
        });
        lastRecPosRef.current = { lat: currentPosition.lat, lng: currentPosition.lng, ts: timestamp };
      }
    }, 1000);
    return () => clearInterval(gpsInterval);
  }, [isRecording]);

  const [soundOn, setSoundOn] = useState(true);
  const toggleSound = useCallback(() => {
    setSoundOn(prev => {
      voiceGuidance.setEnabled(!prev);
      if (prev) voiceGuidance.stop();
      return !prev;
    });
  }, []);

  const [distanceToStep, setDistanceToStep] = useState(0);
  const [hasAnnounced, setHasAnnounced] = useState(false);
  const lastAnnouncedStep = useRef<number>(-1);
  const navigationStartTime = useRef<number>(Date.now());

  // Get current and next step
  const currentStep = activeRoute?.steps[currentStepIndex];
  const nextStep = activeRoute?.steps[currentStepIndex + 1];

  // Announce first instruction when navigation starts
  useEffect(() => {
    if (!isNavigating || !activeRoute || !currentStep) return;

    // Reset navigation start time when navigation begins
    navigationStartTime.current = Date.now();

    // Announce starting instruction
    if (currentStepIndex === 0 && lastAnnouncedStep.current === -1) {
      const message = currentStep.maneuver === 'depart'
        ? `Starting navigation to ${activeRoute.destinationName}. ${currentStep.instruction}`
        : currentStep.instruction;
      voiceGuidance.speak(message, { priority: 'high' });
      lastAnnouncedStep.current = 0;
    }
  }, [isNavigating, activeRoute, currentStep, currentStepIndex]);

  // GPS-based route tracking (real mode)
  useEffect(() => {
    if (!isNavigating || !activeRoute || !currentStep || isDemoMode || !currentPosition) return;

    // Calculate distance to next turn/step
    const distance = calculateDistance(currentPosition, currentStep.coordinates);
    setDistanceToStep(distance);
    updateProgress(currentStepIndex, distance);

    // Voice announcements at specific distances
    if (currentStepIndex !== lastAnnouncedStep.current) {
      // New step - announce at 200m
      if (distance <= 200 && distance > 100) {
        voiceGuidance.announceTurn(currentStep.instruction, distance);
        setHasAnnounced(true);
        lastAnnouncedStep.current = currentStepIndex;
      }
    }

    // Immediate turn announcement at 50m
    if (distance <= 50 && !hasAnnounced) {
      voiceGuidance.announceImmediateTurn(currentStep.instruction);
      setHasAnnounced(true);
    }

    // Auto-advance when close to turn point (within 20m)
    // Guard: don't auto-stop navigation within first 10 seconds (prevents
    // immediate stop when user starts at origin point)
    const timeSinceStart = Date.now() - navigationStartTime.current;
    if (distance <= 20) {
      if (currentStepIndex < activeRoute.steps.length - 1) {
        advanceStep();
        setHasAnnounced(false);
      } else if (timeSinceStart > 10000) {
        // Only stop if we've been navigating for more than 10 seconds
        voiceGuidance.announceArrival(activeRoute.destinationName);
        stopNavigation();
      }
    }
  }, [
    isNavigating,
    activeRoute,
    currentStep,
    currentStepIndex,
    currentPosition,
    isDemoMode,
    advanceStep,
    stopNavigation,
    updateProgress,
    hasAnnounced,
  ]);

  // Demo mode - Auto-advance steps with fixed timer
  useEffect(() => {
    if (!isNavigating || !activeRoute || !isDemoMode) return;

    const timer = setInterval(() => {
      if (currentStepIndex < activeRoute.steps.length - 1) {
        advanceStep();
        // Announce in demo mode too
        const step = activeRoute.steps[currentStepIndex + 1];
        if (step) {
          voiceGuidance.announceTurn(step.instruction, step.distance);
        }
      } else {
        voiceGuidance.announceArrival(activeRoute.destinationName);
        stopNavigation();
      }
    }, 8000);

    return () => clearInterval(timer);
  }, [isNavigating, activeRoute, currentStepIndex, isDemoMode, advanceStep, stopNavigation]);

  // Reset tracking when navigation stops
  useEffect(() => {
    if (!isNavigating) {
      voiceGuidance.stop();
      lastAnnouncedStep.current = -1;
      setHasAnnounced(false);
      setDistanceToStep(0);
    }
  }, [isNavigating]);

  // Cleanup voice on unmount
  useEffect(() => {
    return () => {
      voiceGuidance.stop();
    };
  }, []);

  if (!isNavigating || !activeRoute || !currentStep) {
    return null;
  }

  // Calculate remaining distance and time with validation
  const rawRemainingDistance = activeRoute.steps
    .slice(currentStepIndex)
    .reduce((sum, step) => sum + (step.distance || 0), 0);

  const rawRemainingTime = activeRoute.steps
    .slice(currentStepIndex)
    .reduce((sum, step) => sum + (step.duration || 0), 0);

  // Validate values - cap at reasonable maximums (50km, 10 hours for walking)
  const remainingDistance = Math.min(rawRemainingDistance, 50000);
  const remainingTime = Math.min(rawRemainingTime, 36000);

  // Display distance - use GPS distance if available, otherwise use step distance
  const displayDistance = !isDemoMode && distanceToStep > 0 ? distanceToStep : currentStep.distance;

  // Display speed - use real GPS speed if available (convert m/s to km/h), otherwise show 0
  const displaySpeed = speed !== null && !isDemoMode ? speed * 3.6 : 0;

  // Urgency levels for visual alerts
  const isImminent = displayDistance <= 50 && currentStep.maneuver !== 'depart';
  const isApproaching = displayDistance <= 100 && currentStep.maneuver !== 'depart';

  return (
    <div className={cn('pointer-events-auto', className)}>
      {/* Top instruction bar */}
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -100, opacity: 0 }}
        className={cn(
          'border-b transition-colors duration-300',
          isImminent
            ? 'bg-amber-500/20 border-amber-500/40'
            : 'bg-bg-deep border-glass-border'
        )}
      >
        {/* Imminent turn alert banner */}
        <AnimatePresence>
          {isImminent && nextStep && nextStep.maneuver !== 'straight' && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="bg-amber-500 px-4 py-2 flex items-center gap-2 overflow-hidden"
            >
              <ManeuverIcon type={nextStep.maneuver} className="w-5 h-5 text-black" />
              <span className="text-sm font-bold text-black">
                {nextStep.maneuver === 'turn-left' ? 'Turn left' : nextStep.maneuver === 'turn-right' ? 'Turn right' : 'Turn'} ahead
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center gap-4 px-4 py-3">
          {/* Maneuver icon — pulses when imminent */}
          <div className={cn('flex-shrink-0', isImminent && 'animate-pulse')}>
            <ManeuverIcon
              type={currentStep.maneuver}
              className={cn(
                'w-12 h-12',
                isImminent ? 'text-amber-400' : 'text-white'
              )}
            />
          </div>

          {/* Instruction text with countdown */}
          <div className="flex-1 min-w-0">
            <div className={cn(
              'text-2xl font-bold tabular-nums',
              isImminent ? 'text-amber-400' : 'text-white'
            )}>
              {formatDistance(Math.round(displayDistance))}
            </div>
            <div className="text-sm text-text-secondary truncate">
              {currentStep.instruction}
            </div>
          </div>

          {/* Close button */}
          <button
            onClick={stopNavigation}
            className="p-2 text-text-secondary hover:text-white transition-colors"
          >
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Recording indicator */}
        {isRecording && (
          <div className="flex items-center gap-3 px-4 py-2 bg-red-500/10 border-t border-red-500/20">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs font-semibold text-red-400 uppercase tracking-wider">REC</span>
            <span className="text-xs font-mono text-white">
              {String(Math.floor(recElapsed / 3600)).padStart(2, '0')}:
              {String(Math.floor((recElapsed % 3600) / 60)).padStart(2, '0')}:
              {String(recElapsed % 60).padStart(2, '0')}
            </span>
            <span className="text-xs text-gray-400 ml-auto">
              {(recordingData.distance / 1000).toFixed(2)} km
            </span>
          </div>
        )}

        {/* Next instruction preview */}
        {nextStep && (
          <div className={cn(
            'flex items-center gap-3 px-4 py-2 border-t',
            isApproaching
              ? 'bg-amber-500/10 border-amber-500/20'
              : 'bg-glass-bg border-glass-border'
          )}>
            <span className="text-xs text-text-muted uppercase">Then</span>
            <ManeuverIcon
              type={nextStep.maneuver}
              className={cn(
                'w-5 h-5',
                isApproaching ? 'text-amber-400' : 'text-text-secondary'
              )}
            />
            <span className={cn(
              'text-sm truncate',
              isApproaching ? 'text-amber-300 font-medium' : 'text-text-secondary'
            )}>
              {nextStep.instruction}
            </span>
          </div>
        )}
      </motion.div>

      {/* Bottom status bar */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 bg-glass-bg border-t border-glass-border backdrop-blur-md"
      >
        <div className="flex items-center justify-between px-4 py-3">
          {/* Speed indicator */}
          <div className="flex flex-col items-center">
            <div
              className={cn(
                'w-14 h-14 rounded-full border-2 flex flex-col items-center justify-center',
                'bg-bg-deep',
                displaySpeed >= 4.5 ? 'border-hud-primary' : 'border-glass-border'
              )}
            >
              <span className="text-lg font-bold text-white">{displaySpeed.toFixed(1)}</span>
              <span className="text-[8px] text-text-secondary uppercase">km/h</span>
            </div>
          </div>

          {/* Center: ETA and distance */}
          <div className="flex flex-col items-center">
            <div className="text-2xl font-bold text-white">
              {formatETA(remainingTime)}
            </div>
            <div className="text-sm text-text-secondary">
              {formatTime(remainingTime)} · {formatDistance(remainingDistance)}
            </div>
          </div>

          {/* AQI indicator */}
          <div className="flex flex-col items-center">
            <div
              className={cn(
                'w-14 h-14 rounded-full border-2 flex flex-col items-center justify-center',
                'bg-bg-deep',
                aqi <= 50
                  ? 'border-hud-success'
                  : aqi <= 100
                  ? 'border-hud-warning'
                  : 'border-hud-danger'
              )}
            >
              <span
                className={cn(
                  'text-lg font-bold',
                  aqi <= 50
                    ? 'text-hud-success'
                    : aqi <= 100
                    ? 'text-hud-warning'
                    : 'text-hud-danger'
                )}
              >
                {aqi}
              </span>
              <span className="text-[8px] text-text-secondary uppercase">AQI</span>
            </div>
          </div>
        </div>

        {/* Route progress bar */}
        <div className="h-1 bg-glass-border">
          <motion.div
            className="h-full bg-hud-primary"
            initial={{ width: '0%' }}
            animate={{
              width: `${((currentStepIndex + 1) / activeRoute.steps.length) * 100}%`,
            }}
            transition={{ duration: 0.5 }}
          />
        </div>

        {/* Action buttons */}
        <div className="flex items-center justify-around py-2 border-t border-glass-border">
          <ActionButton icon={<SearchIcon className="w-5 h-5" />} label="Search" />
          <ActionButton
            icon={<SoundIcon className="w-5 h-5" />}
            label={soundOn ? 'Sound On' : 'Sound Off'}
            onClick={toggleSound}
          />
          <ActionButton icon={<RouteIcon className="w-5 h-5" />} label="Routes" />
        </div>
      </motion.div>
    </div>
  );
}

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

function ActionButton({ icon, label, onClick }: ActionButtonProps) {
  return (
    <button
      onClick={onClick}
      className="flex flex-col items-center gap-1 px-4 py-1 text-text-secondary hover:text-hud-primary transition-colors"
    >
      {icon}
      <span className="text-[10px] uppercase">{label}</span>
    </button>
  );
}

interface ManeuverIconProps {
  type: string;
  className?: string;
}

function ManeuverIcon({ type, className }: ManeuverIconProps) {
  switch (type) {
    case 'turn-left':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M14 6v2h-3c-1.1 0-2 .9-2 2v10h-2V10c0-2.21 1.79-4 4-4h3V4l4 3.5-4 3.5v-1z" />
        </svg>
      );
    case 'turn-right':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M10 6v2h3c1.1 0 2 .9 2 2v10h2V10c0-2.21-1.79-4-4-4h-3V4l-4 3.5 4 3.5v-1z" />
        </svg>
      );
    case 'u-turn':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M18 9v12h-2V9c0-2.21-1.79-4-4-4S8 6.79 8 9v4.17l1.59-1.59L11 13l-4 4-4-4 1.41-1.41L6 13.17V9c0-3.31 2.69-6 6-6s6 2.69 6 6z" />
        </svg>
      );
    case 'arrive':
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
        </svg>
      );
    case 'straight':
    default:
      return (
        <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
          <path d="M12 4l-4 4h3v8h2V8h3l-4-4z" />
        </svg>
      );
  }
}

function formatDistance(meters: number): string {
  if (meters < 1000) {
    return `${meters} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
}

function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours} h ${minutes} min`;
  }
  return `${minutes} min`;
}

function formatETA(seconds: number): string {
  const now = new Date();
  const eta = new Date(now.getTime() + seconds * 1000);
  return eta.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

// Icons
function CloseIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </svg>
  );
}

function SoundIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    </svg>
  );
}

function RouteIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={className}>
      <circle cx="6" cy="19" r="3" />
      <path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15" />
      <circle cx="18" cy="5" r="3" />
    </svg>
  );
}

export default WazeStyleNav;
